import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { ShieldCheck, HandHeart, BadgeCheck, Star } from "lucide-react";

export default function Home() {
  const { t, i18n } = useTranslation();
  const [listings, setListings] = useState([]);

  useEffect(() => {
    api.get("/listings").then(r => setListings(r.data || []));
  }, []);

  const reviewCards = useMemo(
    () =>
      listings
        .flatMap((l) =>
          (l.reviews || []).map((r, i) => ({ ...r, key: `${l.listing_id}-${i}` }))
        )
        .slice(0, 4),
    [listings]
  );

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-[88vh] min-h-[600px] w-full overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1680416124510-5eae1beca412?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="StayTLV"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/25" />
        <div className="relative h-full flex items-center justify-center text-center px-6">
          <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-none tracking-tight animate-fade-up" data-testid="hero-title">
            {t("home.tagline")}
          </h1>
        </div>
      </section>

      {/* Featured listings */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {listings.map((l) => <ListingCard key={l.listing_id} listing={l} lang={i18n.language} />)}
        </div>
      </section>

      {/* Trust signals */}
      <section className="bg-[#F4EFE6] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <TrustItem icon={<HandHeart className="h-6 w-6" />} text={t("home.trust1")} />
            <TrustItem icon={<ShieldCheck className="h-6 w-6" />} text={t("home.trust2")} />
            <TrustItem icon={<BadgeCheck className="h-6 w-6" />} text={t("home.trust3")} />
          </div>
        </div>
      </section>

      {/* Reviews */}
      {listings[0]?.reviews?.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {reviewCards.map(r => (
              <div key={r.key} className="bg-white rounded-2xl p-8 border border-[#EAE3D9]">
                <div className="flex gap-0.5 text-[#C86D53] mb-3">
                  {Array.from({ length: r.rating || 5 }).map((_, i) => <Star key={`star-${r.key}-${i}`} className="h-4 w-4 fill-current" />)}
                </div>
                <p className="font-serif text-xl text-[#2C2A29] leading-snug">&ldquo;{i18n.language === "he" ? r.text_he : r.text_en}&rdquo;</p>
                <p className="text-sm text-[#8C7A6B] mt-4">— {r.author}, {r.date}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const TrustItem = ({ icon, text }) => (
  <div className="flex flex-col gap-3">
    <div className="h-12 w-12 rounded-full bg-white text-[#C86D53] flex items-center justify-center">{icon}</div>
    <p className="text-[#2C2A29] leading-relaxed text-base">{text}</p>
  </div>
);
